<template>
  <div class="task">
    <FindBar
      v-if="isFindBarOpen"
      ref="findBarEl"
      container-id="scrollable-document"
      @close="closeFindBar"
    />
    <div id="scrollable-document">
      <RecurrentTaskTopBar
        v-if="!embeddedMode"
        :recurrent-task="value"
        :has-block-active="hasBlockActive"
        @select-all="selectAll"
        @flush="flush"
        @find="openFindBar"
      />
      <div v-if="value" class="infos" @keydown="keydown">
        <TitledDocument
          ref="document"
          :key="value.id"
          :debounce="debounce"
          :templates="templates"
          :value="value"
          :embedded-mode="embeddedMode"
        >
          <template #preTitle>
            <div class="pre_box">
              <TaskIcon :recurring="status" class="large_icon" />
            </div>
          </template>

          <template #postTitle>
            <ContextButton
              class="title-context-button"
              :task="value"
              is-shortcut-enabled
            />
          </template>
        </TitledDocument>
      </div>

      <div v-else class="content">No such recurrent task.</div>
    </div>
  </div>
</template>

<script lang="ts">
import {
  computed,
  defineComponent,
  PropType,
  provide,
  ref,
  toRefs,
  watch,
} from "vue";

import { RecurrentTask } from "@ctrl/controller-rpc";

import { useDocument } from "@/components/document";
import ContextButton from "@/components/elements/ContextButton.vue";
import FindBar from "@/components/find/FindBar.vue";
import { useFindBar } from "@/components/find/useFindBar";
import TaskIcon from "@/components/icons/TaskIcon.vue";
import * as inject from "@/components/inject";
import { Recurrence, recurrenceStatus } from "@/components/recurrence";
import RecurrentTaskTopBar from "@/components/recurrent-task/RecurrentTaskTopBar.vue";
import { TemplateName } from "@/components/templates";
import TitledDocument from "@/components/TitledDocument.vue";
import { keydown } from "@/routes/object";

export default defineComponent({
  components: {
    RecurrentTaskTopBar,
    ContextButton,
    FindBar,
    TaskIcon,
    TitledDocument,
  },
  props: {
    debounce: {
      type: Number,
      default: 5000,
    },
    previousRoute: {
      type: String,
      default: null,
    },
    value: {
      type: Object as PropType<RecurrentTask>,
      required: true,
    },
    embeddedMode: {
      type: Boolean,
      default: false,
    },
  },
  async setup(props) {
    const { value: recurrentTask } = toRefs(props);
    const router = inject.router();
    const document = ref<InstanceType<typeof TitledDocument> | null>(null);
    const { isFindBarOpen, findBarEl, openFindBar, closeFindBar } =
      useFindBar();
    const parent = ref({ kind: "task-rec", id: recurrentTask.value.id });
    provide("parent", parent);
    watch(
      recurrentTask,
      (value) => (parent.value = { kind: "task-rec", id: value.id })
    );
    const status = computed<Recurrence>(() =>
      recurrenceStatus(recurrentTask.value)
    );

    const templates: Array<TemplateName> = ["empty", "embed", "subtasks"];
    return {
      closeFindBar,
      document,
      findBarEl,
      isFindBarOpen,
      keydown,
      openFindBar,
      status,
      templates,
      //@ts-ignore
      ...useDocument<RecurrentTask>(recurrentTask, document, router),
    };
  },
});
</script>

<style scoped lang="scss">
@use "@/styles/event.scss";

.task {
  // Anchor for the find bar.
  position: relative;
}

.title-context-button :deep(button.main-button) {
  padding: 4px;
  border-radius: $border-radius-m;
  cursor: default;
  @include transition(background-color 100ms ease-out);
  &:hover {
    @include themed() {
      background-color: t($grey-10);
    }
  }
}
</style>

<style lang="scss">
#scrollable-document {
  width: 100%;
  box-sizing: border-box;
  display: flex;
  overflow-y: scroll;
  scrollbar-width: none;
}
.pre_box {
  position: absolute;
  inset-inline-start: -36px;
  top: 14px;
}
.large_icon {
  display: flex;
  width: 20px;
  height: 20px;
  svg {
    width: 18px;
    height: 18px;
  }
}
</style>
